const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ErrorReportingService {
  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true';
    this.maxReportsPerSession = 10; // Limit reports to prevent spam
    this.reportCount = 0;
    this.reportedErrors = new Set(); // Track reported errors to prevent duplicates
  }

  // Check if we should report this error
  shouldReportError(error) {
    if (!this.isEnabled) {
      console.log('Error reporting disabled');
      return false;
    }

    if (this.reportCount >= this.maxReportsPerSession) {
      console.log('Max error reports reached for this session');
      return false;
    }

    // Create a unique key for this error
    const errorKey = `${error.name}:${error.message}:${error.stack?.substring(0, 100)}`;
    if (this.reportedErrors.has(errorKey)) {
      console.log('Error already reported in this session');
      return false;
    }

    this.reportedErrors.add(errorKey);
    this.reportCount++;
    return true;
  }

  // Get rich user context for error reporting
  getUserContext() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Also check if admin is impersonating (stored separately by login-as flow)
      const impersonatedBy = localStorage.getItem('pestcrm_impersonated_by') || null;

      return {
        userEmail: user.email || 'anonymous',
        userId: user.id || null,
        userRole: user.role || null,
        userName: user.name || null,
        userBranch: user.branchName || null,
        franchiseeId: user.franchiseeId || null,
        assignedFranchiseeId: user.assignedFranchiseeId || null,
        impersonatedBy: impersonatedBy
      };
    } catch (e) {
      return {
        userEmail: 'anonymous',
        userId: null,
        userRole: null,
        userName: null,
        userBranch: null,
        franchiseeId: null,
        assignedFranchiseeId: null,
        impersonatedBy: null
      };
    }
  }

  // Report a frontend JavaScript error
  async reportFrontendError(error, errorInfo = {}) {
    if (!this.shouldReportError(error)) {
      return false;
    }

    try {
      const userContext = this.getUserContext();

      const errorData = {
        error: {
          name: error.name || 'Unknown Error',
          message: error.message || 'Unknown error message',
          stack: error.stack || 'No stack trace available'
        },
        url: window.location.href,
        userAgent: navigator.userAgent,
        userEmail: userContext.userEmail,
        userContext,
        componentStack: errorInfo.componentStack || null,
        lineno: error.lineno || null,
        colno: error.colno || null,
        filename: error.filename || null,
        timestamp: new Date().toISOString()
      };

      console.log('Reporting frontend error:', errorData);

      const response = await fetch(`${API_BASE_URL}/error-reporting/frontend-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      });

      if (!response.ok) {
        throw new Error(`Failed to report error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Error reported successfully:', result);
      return true;

    } catch (reportingError) {
      console.error('Failed to report frontend error:', reportingError);
      return false;
    }
  }

  // Report an API call error
  async reportApiError(apiError, requestDetails = {}) {
    if (!this.shouldReportError(apiError)) {
      return false;
    }

    try {
      const userContext = this.getUserContext();

      const errorData = {
        endpoint: requestDetails.endpoint || 'unknown',
        method: requestDetails.method || 'unknown',
        status: requestDetails.status || null,
        error: {
          name: apiError.name || 'API Error',
          message: apiError.message || 'Unknown API error'
        },
        url: window.location.href,
        userEmail: userContext.userEmail,
        userContext,
        requestBody: requestDetails.requestBody || null,
        response: requestDetails.response || null,
        timestamp: new Date().toISOString()
      };

      console.log('Reporting API error:', errorData);

      const response = await fetch(`${API_BASE_URL}/error-reporting/frontend-api-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      });

      if (!response.ok) {
        throw new Error(`Failed to report API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('API error reported successfully:', result);
      return true;

    } catch (reportingError) {
      console.error('Failed to report API error:', reportingError);
      return false;
    }
  }

  // Set up global error handlers
  setupGlobalErrorHandlers() {
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      const error = new Error(event.message);
      error.name = 'JavaScript Error';
      error.filename = event.filename;
      error.lineno = event.lineno;
      error.colno = event.colno;
      error.stack = event.error?.stack || 'No stack trace available';

      this.reportFrontendError(error);
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = new Error(event.reason?.message || 'Unhandled Promise Rejection');
      error.name = 'Promise Rejection';
      error.stack = event.reason?.stack || 'No stack trace available';

      this.reportFrontendError(error);

      // Prevent the default console error
      event.preventDefault();
    });
  }

  // Test error reporting (development only)
  async testErrorReporting() {
    if (process.env.NODE_ENV !== 'development') {
      console.log('Test error reporting only available in development');
      return;
    }

    console.log('Testing error reporting...');

    // Test frontend error
    const testError = new Error('Test frontend error message');
    testError.name = 'TestError';
    await this.reportFrontendError(testError, {
      componentStack: 'TestComponent > App'
    });

    // Test API error
    const testApiError = new Error('Test API error message');
    await this.reportApiError(testApiError, {
      endpoint: '/api/test',
      method: 'GET',
      status: 500,
      response: { error: 'Test error response' }
    });

    console.log('Error reporting tests completed');
  }
}

// Create singleton instance
const errorReportingService = new ErrorReportingService();

// Setup global error handlers when the service is imported
errorReportingService.setupGlobalErrorHandlers();

export default errorReportingService;